import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AuthorizationService } from '../../services/authorization.service';
import { UserService } from '../../../../services/user.service';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ExcelExporterService } from '../../../../shared/services/excel-exporter';
import { ButtonComponent } from '@ppl/ui';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { AuthBarChartComponent } from '../../auth-bar-chart/auth-bar-chart.component';
import { ChartDataset } from 'chart.js';
import { LookupService } from '../../../../services/lookup.service';

@Component({
  selector: 'ppl-monthly-spending-plan-summary',
  standalone: true,
  imports: [
            NzGridModule, CommonModule, FormsModule, ButtonComponent,
            AuthBarChartComponent
  ],
  templateUrl: './monthly-spending-plan-summary.component.html',
  styleUrl: './monthly-spending-plan-summary.component.css'
})
export class MonthlySpendingPlanSummaryComponent implements OnInit, OnChanges {
  @Input() selectedParticipantId: number = 0;
  @Input() clientAuthNumber: string | null = null;
  @Input() providerId: number | null = null;
  @Input() code: string | null = null;
  @Input() servicegroupLkp: string | null = null;
  @Input() fromDate: string | null = null;
  @Input() toDate: string | null = null;
  @Output() spentCurrentMonthChange = new EventEmitter<number>();
  /** Human-readable span of the breakdown, e.g. "Oct 2026" or "Jan 2026 - Mar 2026". */
  periodLabel: string = '';
  totalBudgetedAmount: number = 0;
  monthArraySet: Set<string> = new Set();
  programCode: string = '';
  monthlySpendingData: any[] = [];
  totalMonthlySpentMap: Map<string, number> = new Map();
  spentCurrentMonth: number = 0;
  minAuthStartDate: Date | null = null;
  availableMonthYearsData: string[] = [];
  barChartLabels: (string | string[])[] = [];
  barChartData: ChartDataset<'bar'>[] = [];
  serviceGroups: any[] = [];

  /*
   * Categorical slots, assigned by the category's fixed position in the lookup so a
   * filter never repaints the survivors. This trio clears the colour checks in light
   * mode (worst adjacent CVD dE 15.8); a programme with more service groups than slots
   * gets the neutral rather than a generated hue.
   */
  private readonly categoryPalette = ['#1ABBB4', '#7A6FE0', '#E0713A'];
  private readonly categoryNeutral = '#8C8C8C';

  private lookupService = inject(LookupService);
  private dateFormatter = inject(DateTimeFormatterService);

  constructor(private authService: AuthorizationService,
    private userService: UserService, private excelExporter: ExcelExporterService) {}

  ngOnInit(): void {
    const program = this.userService.getSelectedProgram();
    this.programCode = program?.program_code ?? '';
    this.loadConsumerMonthlySpentData();
    this.serviceGroups = this.lookupService.getLookupValue('Service_Group');
  }

  ngOnChanges(changes: SimpleChanges): void {
    const filterInputs = ['selectedParticipantId', 'clientAuthNumber', 'providerId', 'code', 'servicegroupLkp', 'fromDate', 'toDate'];
    const filtersChanged = filterInputs.some(input => changes[input] && !changes[input].firstChange);
    if (filtersChanged) {
      this.loadConsumerMonthlySpentData();
    }
  }


  loadConsumerMonthlySpentData() {
    const request = {
      participantId: this.selectedParticipantId,
      clientAuthNumber: this.clientAuthNumber ?? null,
      providerId: this.providerId ?? null,
      code: this.code ?? null,
      servicegroupLkp: this.servicegroupLkp ?? null,
      fromDate: this.fromDate ?? null,
      toDate: this.toDate ?? null
    };

    this.authService.getConsumerMonthlyChartData(this.programCode, request).subscribe((data: any) => {
      this.monthlySpendingData = data.responsedata;
      if (this.monthlySpendingData?.length > 0) {
        const minStartDate = this.monthlySpendingData.reduce((minDate: Date, item: any) => {
          const itemDate = this.dateFormatter.createDateInUTC(item.startDate);
          return itemDate < minDate ? itemDate : minDate;
        }
        , this.dateFormatter.createDateInUTC(new Date().toDateString()));
        this.minAuthStartDate = minStartDate;
      }

      this.setAvailableMonthYears();
    });
  }

  /*
   * One column per month in the From/To filter range, both months inclusive, so a range
   * inside a single month shows just that month. A cleared date falls back to the
   * matching edge of the other date's year (or the current year if both are cleared).
   */
  setAvailableMonthYears() {
    const from = this.toMonthStart(this.fromDate);
    const to = this.toMonthStart(this.toDate);
    const year = (from ?? to)?.getFullYear() ?? new Date().getFullYear();
    const start = from ?? new Date(to ? to.getFullYear() : year, 0, 1);
    const end = to ?? new Date(from ? from.getFullYear() : year, 11, 1);

    const monthYears: string[] = [];
    for (const month = new Date(start); month <= end; month.setMonth(month.getMonth() + 1)) {
      monthYears.push(this.monthYearKey(month));
    }
    this.availableMonthYearsData = monthYears;

    const startLabel = this.monthYearKey(start).replace('-', ' ');
    const endLabel = this.monthYearKey(end).replace('-', ' ');
    this.periodLabel = startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;

    this.setMonthlyData();
    this.generateBarChart();
    this.spentCurrentMonthChange.emit(this.spentCurrentMonth);
  }

  /** First of the month for a yyyy-MM-dd filter date, built locally to avoid UTC day shifts. */
  private toMonthStart(date: string | null): Date | null {
    if (!date) {
      return null;
    }
    const [year, month] = date.split('-').map(Number);
    return year && month ? new Date(year, month - 1, 1) : null;
  }

  /** Same "Mon-YYYY" key the API uses for monthYear, e.g. "Oct-2026". */
  private monthYearKey(date: Date): string {
    return `${date.toLocaleString('default', { month: 'short' })}-${date.getFullYear()}`;
  }

  setMonthlyData() {
    this.monthlySpendingData.forEach(item => {
      item.categoryName = item.category;
      item['monthlyMap'] = new Map<string, any>();
      if (item.monthlyData.length > 0) {
        item.monthlyMap = new Map(item.monthlyData.map((monthItem: any) => [monthItem.monthYear, monthItem]));
      }
    });
    this.monthArraySet = new Set(this.monthlySpendingData.flatMap(item => item.monthlyData.map((monthItem: any) => monthItem.monthYear)));
    this.totalMonthlySpentMap.clear();
    this.spentCurrentMonth = 0;
    this.totalBudgetedAmount = this.monthlySpendingData.reduce((sum, item) => sum + (item.totalAuth || 0), 0);
    let allMonthsData = this.monthlySpendingData.flatMap(item => item.monthlyData);
    const currentMonthYear = new Date().toLocaleString('default', { month: 'short', year: 'numeric' }).replace(' ', '-');
    this.monthArraySet.forEach(monthYear => {
      let monthData = allMonthsData.filter((monthItem: any) => monthItem.monthYear === monthYear);
      let totalSpent = monthData.reduce((sum: number, item: any) => sum + (item.spent || 0), 0);
      this.totalMonthlySpentMap.set(monthYear, totalSpent);
      if (monthYear === currentMonthYear) {
        this.spentCurrentMonth = totalSpent;
      }
    });
  }

  exportToExcel() {
    // Prepare data for export. we need to flatten the data structure.
    const exportData: any[] = [];
    const totalRow: any = {
      "Spending Plan": "Total Budget",
      "Budgeted": this.formatCurrency(this.totalBudgetedAmount)
    }
    this.monthlySpendingData.forEach(item => {
      const row: any = {
        'Spending Plan': item.clientAuthNumber,
        'Category': item.categoryName,
        'Service Code & Description': item.code + ' | ' + item.description,
        'Budgeted': this.formatCurrency(item.totalAuth)
      };
      this.availableMonthYearsData.forEach(monthYear => {
        const monthData = item.monthlyMap.get(monthYear);
        row[`${monthYear}`] = this.formatCurrency(monthData?.spent);
      });
      exportData.push(row);
    });

    for (const key of this.availableMonthYearsData) {
      totalRow[key] = this.formatCurrency(this.totalMonthlySpentMap.get(key));
    }

    exportData.push(totalRow);
    this.excelExporter.exportData(exportData, 'Monthly_Spending_Plan_Summary');
  }

  /** Same $1,234.56 shape as the on-screen table; sums of float spends otherwise export as 474.58299999999997. */
  private formatCurrency(value: number | null | undefined): string {
    return (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  private categoryColor(code: string): string {
    const slot = this.serviceGroups.findIndex(sg => sg.lkpValueCode === code);
    return this.categoryPalette[slot] ?? this.categoryNeutral;
  }

  private generateBarChart() {
    this.barChartLabels = this.availableMonthYearsData;
    this.barChartData = [];

    let dataSet: ChartDataset<'bar'>[] = [];
    this.serviceGroups.forEach(sg => {
      const data: number[] = [];
      this.availableMonthYearsData.forEach(monthYear => {
        let monthTotal = 0;
        this.monthlySpendingData.forEach(item => {
          if (item.category === sg.lkpValueName) {
            const monthData = item.monthlyMap.get(monthYear);
            monthTotal += monthData ? monthData.spent : 0;
          }
        });
        data.push(monthTotal);
      });

      dataSet.push({
        label: sg.lkpValueName,
        data: data,
        backgroundColor: this.categoryColor(sg.lkpValueCode),
        // 2px surface gap separating stacked segments and neighbouring columns.
        borderColor: '#ffffff',
        borderWidth: 2
      });
    });

    this.barChartData = dataSet;
  }
}
